/**
 * campsAlong.ts — the camps a trip actually passes.
 *
 * Two jobs, both pure:
 *
 *  1. Turn the two campsite shapes the planner sees (bounds-search entities and
 *     /routing/suggestions candidates) into one `CampOption`, with honest
 *     labels. "Free" is only ever said when the fee sweep says FREE
 *     (`fee_verdict`) — a free_camping entity_type or a stray feature is not
 *     enough (5 of 6 sampled "free" parks were paid).
 *
 *  2. Corridor geometry: the line each leg follows (the routed road when we
 *     have it, a straight line otherwise), boxes that cover it for the
 *     bounds-search, and which camps sit within N km of it — in the order you
 *     reach them. "Camps along route" used to send only start + end to the
 *     server and got 493 results down the Hume for a coast trip; building the
 *     corridor from every stop in order fixes that.
 */

import { haversineDistanceKm, pointToSegmentKm } from './tripPlanner';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface BoundsBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface CampOption {
  id: number;
  name: string;
  lat: number;
  lng: number;
  /** entity_type as stored (caravan_park, campground, free_camping, …). */
  type: string | null;
  /** A real photo URL, or null (the card shows the default hero). */
  image: string | null;
  /** Web-checked fee status (FREE / PAID / DONATION / PERMIT / CLOSED / UNKNOWN). */
  feeVerdict: string | null;
  minPrice: number | null;
  /** true = powered sites, false = unpowered only, null = we don't know. */
  powered: boolean | null;
  bookingUrl: string | null;
  town: string | null;
  rating: number | null;
  /** A "Camping Fee" / "Fees apply" feature is on the listing. */
  feeFeature: boolean;
  /**
   * Other usable photos of the same place, best guess first. The card picks
   * an outdoor one from these (a caravan park reads as a camp from outside,
   * not from its cabin bedroom).
   */
  gallery?: string[];
  /**
   * Up to three facilities worth deciding on ("Showers", "Toilets", "Pets
   * OK"), in that order, from the listing's own features. Shown where a price
   * would go when we don't have one.
   */
  amenities?: string[];
}

export interface CampOnLine extends CampOption {
  /** Shortest distance to the line, km. */
  offKm: number;
  /** Distance along the line to the closest point, km (for ordering). */
  alongKm: number;
}

/** Camping types: what "camps" means on a night card and a leg search. */
export const CAMP_TYPES = new Set(['caravan_park', 'campground', 'campsite', 'free_camping']);

export function isCampType(type: string | null | undefined): boolean {
  return !!type && CAMP_TYPES.has(type);
}

/* ── Normalising ─────────────────────────────────────────────────────────── */

const JUNK_IMAGE = new Set(['api', 'web', 'portal', 'custom', 'manual', 'n/a', 'na', 'none', 'null', 'undefined', 'image', 'photo']);

function usableImage(src: unknown): src is string {
  if (typeof src !== 'string') return false;
  const t = src.trim();
  if (!t || JUNK_IMAGE.has(t.toLowerCase())) return false;
  return /[./]/.test(t);
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) ? n : null;
};

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

type FeatureLike = { desc?: string | null; description?: string | null } | string;

function featureTexts(features: unknown): string[] {
  if (!Array.isArray(features)) return [];
  return (features as FeatureLike[])
    .map((f) => (typeof f === 'string' ? f : f?.desc || f?.description || ''))
    .map((s) => String(s).toLowerCase())
    .filter(Boolean);
}

/**
 * Photo URLs that name an indoor shot (a cabin bedroom on a caravan park's
 * card reads as a motel). Only the filename/path is checked.
 */
export function looksInterior(src: string | null | undefined): boolean {
  if (!src) return false;
  const file = src.split('?')[0].toLowerCase().split('/').pop() || '';
  return /(^|[_\-. ])(beds?|bedrooms?|bathrooms?|ensuite|kitchen|kitchenette|interior|lounge|livingroom|suite|bunks?)([_\-. 0-9]|$)/.test(file);
}

/** Bounds-search / search entity → CampOption. null when it has no location. */
export function campFromEntity(e: any): CampOption | null {
  if (!e) return null;
  const id = num(e.id ?? e.entity_id);
  const addr = Array.isArray(e.addresses) ? e.addresses[0] : null;
  const lat = num(addr?.gps_latitude ?? e.lat ?? e.latitude);
  const lng = num(addr?.gps_longitude ?? e.lng ?? e.longitude);
  if (id === null || lat === null || lng === null) return null;

  let image: string | null = null;
  let gallery: string[] = [];
  if (Array.isArray(e.gallery_images) && e.gallery_images.length) {
    const imgs = (e.gallery_images as any[]).filter((g) => usableImage(g?.image_src));
    // A camp reads as a camp from outside: skip bedroom/bathroom shots when
    // the gallery has anything else.
    const outdoor = imgs.filter((g) => !looksInterior(g.image_src));
    const pool = outdoor.length ? outdoor : imgs;
    const featured = pool.find((g) => g.is_featured);
    image = (featured ?? pool[0])?.image_src ?? null;
    gallery = [...outdoor, ...imgs.filter((g) => looksInterior(g.image_src))].map((g) => String(g.image_src));
  }
  if (!image && usableImage(e.image_src)) image = e.image_src;
  if (!image && usableImage(e.display_src)) image = e.display_src;
  if (usableImage(e.image_src) && !gallery.includes(e.image_src)) gallery.push(e.image_src);
  // The chosen photo first, then the rest; a handful is plenty to pick from.
  if (image) gallery = [image, ...gallery.filter((g) => g !== image)];
  gallery = gallery.slice(0, 6);

  const feats = featureTexts(e.features);
  const hasPower = feats.some((f) => /^power|powered/.test(f));
  const feeFeature = feats.some((f) => f.includes('camping fee') || f.includes('fees apply') || f.includes('fee applies'));
  const amenities = campAmenities(feats);

  return {
    id,
    name: str(e.name) ?? 'Campsite',
    lat,
    lng,
    type: str(e.entity_type),
    image,
    feeVerdict: str(e.fee_verdict)?.toUpperCase() ?? null,
    minPrice: num(e.min_price ?? e.from_price),
    // Only claim "unpowered" when the listing actually lists its facilities.
    powered: hasPower ? true : feats.length >= 3 ? false : null,
    bookingUrl: str(e.booking_url),
    town: str(addr?.town) ?? str(e.town),
    rating: num(e.rating ?? e.avg_rating),
    feeFeature,
    ...(gallery.length > 1 ? { gallery } : {}),
    ...(amenities.length ? { amenities } : {}),
  };
}

/** Facilities a camper decides on, most decisive first. Lower-cased feature text in. */
const AMENITY_RULES: [RegExp, string][] = [
  [/^(hot )?showers?\b/, 'Showers'],
  [/^(flush )?toilets?\b|^drop toilet/, 'Toilets'],
  [/^pets? (welcome|allowed|ok|friendly)|^dog friendly/, 'Pets OK'],
  [/^drinking water|^potable water/, 'Water'],
  [/^dump point/, 'Dump point'],
];

export function campAmenities(featureTexts: string[]): string[] {
  const out: string[] = [];
  for (const [re, label] of AMENITY_RULES) {
    if (featureTexts.some((f) => re.test(f)) && !out.includes(label)) out.push(label);
    if (out.length === 3) break;
  }
  return out;
}

export interface CandidateLike {
  entity_id: number;
  name: string;
  lat: number;
  lng: number;
  image_src?: string;
  has_powered?: boolean;
  is_free?: boolean;
  min_price?: number | null;
  booking_url?: string | null;
  entity_type?: string | null;
  suburb?: string | null;
  town?: string;
  rating?: number;
}

/** /routing/suggestions candidate → CampOption. `is_free` is NOT trusted. */
export function campFromCandidate(c: CandidateLike): CampOption {
  return {
    id: c.entity_id,
    name: c.name,
    lat: c.lat,
    lng: c.lng,
    type: c.entity_type ?? null,
    image: usableImage(c.image_src) ? c.image_src! : null,
    feeVerdict: null,
    minPrice: typeof c.min_price === 'number' && c.min_price > 0 ? c.min_price : null,
    powered: typeof c.has_powered === 'boolean' ? c.has_powered : null,
    bookingUrl: c.booking_url ?? null,
    town: c.suburb || c.town || null,
    rating: typeof c.rating === 'number' && c.rating > 0 ? c.rating : null,
    feeFeature: false,
  };
}

/**
 * Ranked picks first (server order), then the nearby-only extras. Fields the
 * ranked list lacks (fee verdict, photo, power) are filled from the nearby
 * copy of the same listing.
 */
export function mergeCampOptions(ranked: CampOption[], nearby: CampOption[]): CampOption[] {
  const byId = new Map(nearby.map((c) => [c.id, c]));
  const seen = new Set<number>();
  const out: CampOption[] = [];
  for (const r of ranked) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    const n = byId.get(r.id);
    out.push(
      n
        ? {
            ...r,
            feeVerdict: r.feeVerdict ?? n.feeVerdict,
            image: r.image ?? n.image,
            gallery: r.gallery ?? n.gallery,
            powered: r.powered ?? n.powered,
            bookingUrl: r.bookingUrl ?? n.bookingUrl,
            town: r.town ?? n.town,
            feeFeature: r.feeFeature || n.feeFeature,
            minPrice: r.minPrice ?? n.minPrice,
            amenities: r.amenities ?? n.amenities,
            rating: r.rating ?? n.rating,
          }
        : r
    );
  }
  for (const n of nearby) {
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    out.push(n);
  }
  return out;
}

/**
 * Nearby camps for a night, best first: open camping listings only, closer is
 * better, a real photo is worth ~8 km (a card you can picture beats a name).
 */
export function rankNearby(options: CampOption[], point: LatLng, radiusKm = 25): CampOption[] {
  return options
    .filter((c) => isCampType(c.type) && c.feeVerdict !== 'CLOSED')
    .map((c) => ({ c, d: haversineDistanceKm(point.lat, point.lng, c.lat, c.lng) }))
    .filter((x) => x.d <= radiusKm)
    .sort((a, b) => a.d + photoPenalty(a.c) - (b.d + photoPenalty(b.c)))
    .map((x) => x.c);
}

/** An outdoor photo is worth ~8 km; an indoor one ~4 km; none, nothing. */
function photoPenalty(c: Pick<CampOption, 'image'>): number {
  if (!c.image) return 8;
  return looksInterior(c.image) ? 4 : 0;
}

/* ── Labels ──────────────────────────────────────────────────────────────── */

export type FeeTone = 'free' | 'paid' | 'note' | 'closed' | 'unknown';

/** A pub, hotel or motel that lets you camp out the back. */
const VENUE_RE = /\b(hotel|pub|tavern|inn|motel)\b/i;
const REST_AREA_RE = /\b(rest area|rest stop|truck stop|parking bay|roadside stop)\b/i;
const isVenueName = (name?: string | null) =>
  !!name && VENUE_RE.test(name) && !/caravan|camp|holiday park|tourist park/i.test(name);

type FeeInput = Pick<CampOption, 'feeVerdict' | 'minPrice' | 'bookingUrl' | 'feeFeature'> & { type?: string | null; name?: string | null };

/**
 * The price. "From $38" when the listing has a rate; "Free" only on a FREE
 * verdict — never from the type, a feature or the name. A pub's FREE verdict
 * isn't trusted either: "free" there usually means "if you buy a meal", and
 * one wrong Free beside a paid pub undoes trust in every other one.
 */
export function campFeeLabel(c: FeeInput): { text: string; tone: FeeTone } | null {
  const v = (c.feeVerdict || '').toUpperCase();
  if (v === 'CLOSED') return { text: 'Closed', tone: 'closed' };
  if (typeof c.minPrice === 'number' && c.minPrice > 0) return { text: `From $${Math.round(c.minPrice)}`, tone: 'paid' };
  if (v === 'FREE' && !isVenueName(c.name)) return { text: 'Free', tone: 'free' };
  if (v === 'DONATION') return { text: 'By donation', tone: 'note' };
  if (v === 'PERMIT') return { text: 'Permit needed', tone: 'note' };
  return null;
}

/**
 * The first thing on every camp row, always in the same place: a rate, a
 * verified Free, or "Price on listing" when we hold no rate. The slot never
 * changes meaning from one row to the next.
 */
export function campPriceSlot(c: FeeInput): { text: string; tone: FeeTone } {
  return campFeeLabel(c) ?? { text: 'Price on listing', tone: 'unknown' };
}

const TYPE_LABEL: Record<string, string> = {
  caravan_park: 'Caravan park',
  campground: 'Campground',
  campsite: 'Campsite',
  accom: 'Stay',
  accommodation: 'Stay',
};

/**
 * What kind of place it is, in plain words. The listing type alone said
 * "Free camp" on a pub ("Bellbird Hotel"), so the name gets a say: pubs read
 * "Pub stopover", rest areas "Rest area", free_camping "Camp area" (whether
 * it's free is the price slot's job, said once).
 */
export function campTypeLabel(c: Pick<CampOption, 'type' | 'feeVerdict'> & { name?: string | null }): string {
  const name = c.name ?? '';
  if (isVenueName(name)) return 'Pub stopover';
  if (REST_AREA_RE.test(name)) return 'Rest area';
  // "Free" lives in the price slot; the type never repeats it.
  if (c.type === 'free_camping') return 'Camp area';
  return TYPE_LABEL[c.type ?? ''] ?? 'Campsite';
}

/** "Powered" / "No power" — words, not a coloured bolt. */
export function campPowerLabel(c: Pick<CampOption, 'powered'>): string | null {
  if (c.powered === true) return 'Powered';
  if (c.powered === false) return 'No power';
  return null;
}

/* ── Duplicates ──────────────────────────────────────────────────────────── */

const normName = (s: string) =>
  s
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(the|no|number)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const prev = new Array(b.length + 1).fill(0).map((_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length];
}

/** Same place, spelt twice? ("Murrungowar Rest Area" / "Murrungower Rest Area"). */
export function similarCampNames(a: string, b: string): boolean {
  const x = normName(a);
  const y = normName(b);
  if (!x || !y) return false;
  if (x === y) return true;
  // Numbered siblings ("Rest Area No 1" / "No 2") are different places.
  const dx = x.match(/\d+/g)?.join(',') ?? '';
  const dy = y.match(/\d+/g)?.join(',') ?? '';
  if (dx !== dy) return false;
  if (x.length >= 8 && y.length >= 8 && (x.includes(y) || y.includes(x))) return true;
  const longest = Math.max(x.length, y.length);
  return editDistance(x, y) / longest <= 0.15;
}

/** How much a listing tells you — the richer copy of a duplicate wins. */
const richness = (c: CampOption) =>
  (c.image ? 4 : 0) + (c.minPrice ? 2 : 0) + (c.feeVerdict ? 2 : 0) + (c.bookingUrl ? 1 : 0) + (c.powered !== null ? 1 : 0) + (c.rating ? 1 : 0);

/**
 * Drop listings that are the same place twice: a similar name within
 * `withinM` metres. A duplicate in a ranked list reads as broken data. Order
 * is kept (the first copy's slot), with the richer copy's details.
 */
export function dedupeCamps<T extends CampOption>(items: T[], withinM = 200): T[] {
  const out: T[] = [];
  for (const c of items) {
    const i = out.findIndex(
      (o) => o.id === c.id || (haversineDistanceKm(o.lat, o.lng, c.lat, c.lng) * 1000 <= withinM && similarCampNames(o.name, c.name))
    );
    if (i < 0) out.push(c);
    else if (isRicher(c, out[i])) out[i] = { ...c, ...pickPosition(out[i]) };
  }
  return out;
}
function isRicher(a: CampOption, b: CampOption) {
  return richness(a) > richness(b);
}
/** Keep the first copy's place in a measured list (offKm / alongKm). */
function pickPosition<T>(o: T): Partial<T> {
  const r: Record<string, unknown> = {};
  const src = o as unknown as Record<string, unknown>;
  if ('offKm' in src) r.offKm = src.offKm;
  if ('alongKm' in src) r.alongKm = src.alongKm;
  return r as Partial<T>;
}

/* ── The nights at a glance ──────────────────────────────────────────────── */

export interface NightsSummary {
  nights: number;
  free: number;
  powered: number;
  booked: number;
}

/**
 * "3 nights · 2 free · 1 powered" — counted from the camps on the cards.
 * Free only counts a FREE verdict.
 */
export function summariseNights(cards: { nights: number; camp: Pick<CampOption, 'feeVerdict' | 'powered'> | null; committed: boolean }[]): NightsSummary {
  let nights = 0;
  let free = 0;
  let powered = 0;
  let booked = 0;
  for (const c of cards) {
    const n = Math.max(1, c.nights);
    nights += n;
    if (!c.camp) continue;
    if ((c.camp.feeVerdict || '').toUpperCase() === 'FREE') free += n;
    if (c.camp.powered === true) powered += n;
    if (c.committed) booked += n;
  }
  return { nights, free, powered, booked };
}

/** "1.2 km" / "12 km" / "<1 km". */
export function formatKm(km: number): string {
  if (!Number.isFinite(km)) return '';
  if (km < 1) return km < 0.3 ? '<1 km' : `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/**
 * How far a camp sits from the road — one phrase everywhere (night card, swap
 * sheet, leg list): "On route" under 300 m, else "1.2 km off route".
 */
export function offRouteLabel(km: number): string {
  if (!Number.isFinite(km)) return '';
  return km < 0.3 ? 'On route' : `${formatKm(km)} off route`;
}

/**
 * The leg list's detour: there and back to the road, so twice the camp's
 * distance off it ("10 km detour"). "On route" under 300 m.
 */
export function detourLabel(offKm: number): string {
  if (!Number.isFinite(offKm)) return '';
  return offKm < 0.3 ? 'On route' : `${formatKm(offKm * 2)} detour`;
}

/**
 * Where on the leg a camp sits, said from the town you leave: "27 km from
 * Lakes Entrance". ("27 km in" read as jargon.)
 */
export function legPositionLabel(alongKm: number, fromName: string): string {
  if (!Number.isFinite(alongKm)) return '';
  if (alongKm < 1) return `Leaving ${fromName}`;
  return `${formatKm(alongKm)} from ${fromName}`;
}

/* ── Leg list: trim, sort, filter ────────────────────────────────────────── */

/**
 * Camps in the leg's two towns belong to those nights (the night card's Swap
 * lists them). Left in, "add a night on this leg" offered a park 1 km from
 * tonight's camp — two overnights a minute apart. Short legs keep everything.
 */
export function trimLegEnds<T extends { alongKm: number }>(items: T[], legKm: number, endKm = 6): T[] {
  if (!(legKm > endKm * 3)) return items;
  return items.filter((c) => c.alongKm >= endKm && c.alongKm <= legKm - endKm);
}

/**
 * The camp to suggest on a long leg: near the halfway mark, close to the
 * road, and one we can say something about (a price or fee verdict, a
 * photo). null on legs too short to break (under ~2 hours of road) or with
 * nothing near the road.
 */
export function recommendLegCamp<T extends CampOption & { alongKm: number; offKm: number }>(items: T[], legKm: number): T | null {
  if (!(legKm >= 150)) return null;
  let best: T | null = null;
  let bestScore = Infinity;
  for (const c of items) {
    if (c.offKm > 5 || c.feeVerdict === 'CLOSED') continue;
    const fromHalf = Math.abs(c.alongKm - legKm / 2) / legKm; // 0 at halfway, 0.5 at either end
    if (fromHalf > 0.3) continue;
    const known = (c.minPrice || c.feeVerdict ? 0 : 0.06) + (c.image ? 0 : 0.04) + (c.powered === null ? 0.02 : 0);
    const score = fromHalf + known + c.offKm / 100;
    if (score < bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

export type LegSort = 'order' | 'closest';
export interface CampFilter {
  powered?: boolean;
  bookable?: boolean;
}

export function sortLegCamps<T extends { alongKm: number; offKm: number }>(items: T[], sort: LegSort): T[] {
  const out = [...items];
  if (sort === 'closest') out.sort((a, b) => a.offKm - b.offKm || a.alongKm - b.alongKm);
  else out.sort((a, b) => a.alongKm - b.alongKm);
  return out;
}

export function filterCamps<T extends Pick<CampOption, 'powered' | 'bookingUrl'>>(items: T[], f: CampFilter): T[] {
  return items.filter((c) => (!f.powered || c.powered === true) && (!f.bookable || !!c.bookingUrl));
}

/* ── Corridor geometry ───────────────────────────────────────────────────── */

export interface LegLine {
  fromId: string;
  toId: string;
  points: LatLng[];
  /** true = follows the routed road; false = straight line between stops. */
  road: boolean;
}

interface StopLike {
  id: string;
  lat?: number;
  lng?: number;
}

const located = (s: StopLike): s is StopLike & LatLng =>
  typeof s.lat === 'number' && typeof s.lng === 'number';

/**
 * One line per leg, in trip order. The vehicle route (sliced at each stop's
 * vertex) wins, then Google's drawn route, then a straight line. Returns null
 * until every stop is located.
 */
export function buildLegLines(
  stops: StopLike[],
  opts: {
    stopIdsKey: string;
    vehicle?: { key: string; polyline: LatLng[]; vertexIndex: number[] } | null;
    google?: { key: string; paths?: LatLng[][] } | null;
  }
): LegLine[] | null {
  if (stops.length < 2 || !stops.every(located)) return null;
  const pts = stops as (StopLike & LatLng)[];
  const v = opts.vehicle;
  const useVehicle =
    !!v && v.key === opts.stopIdsKey && v.vertexIndex.length === stops.length && v.polyline.length >= 2;
  const g = opts.google;
  const useGoogle = !useVehicle && !!g && g.key === opts.stopIdsKey && g.paths?.length === stops.length - 1;
  const out: LegLine[] = [];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    let line: LatLng[] | null = null;
    if (useVehicle) {
      const s = v!.vertexIndex[i];
      const e = v!.vertexIndex[i + 1];
      if (e > s) line = v!.polyline.slice(s, e + 1);
    } else if (useGoogle) {
      const p = g!.paths![i];
      if (p && p.length >= 2) line = p;
    }
    out.push({
      fromId: a.id,
      toId: b.id,
      points: line ?? [{ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }],
      road: !!line,
    });
  }
  return out;
}

/** Fewer vertices, same shape (keeps every Nth + the last) — for long routes. */
export function thinLine(points: LatLng[], maxPoints = 1500): LatLng[] {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  const out = points.filter((_, i) => i % step === 0);
  const last = points[points.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

const KM_PER_DEG_LAT = 111.32;

/** Extra points every `stepKm` along long segments (a straight leg is 2 points). */
export function densifyLine(points: LatLng[], stepKm = 15): LatLng[] {
  if (points.length < 2) return points;
  const out: LatLng[] = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const km = haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
    const n = Math.floor(km / stepKm);
    for (let k = 1; k <= n; k += 1) {
      const f = k / (n + 1);
      out.push({ lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f });
    }
    out.push(b);
  }
  return out;
}

/**
 * Boxes that cover the line plus `padKm` either side, one per ~`chunkKm` of
 * line — each small enough for one bounds-search page (the endpoint errors
 * on boxes holding more than a page, e.g. all of Sydney).
 */
export function corridorBoxes(input: LatLng[], padKm: number, chunkKm = 60): BoundsBox[] {
  if (!input.length) return [];
  const points = densifyLine(input, Math.max(5, chunkKm / 4));
  const boxes: BoundsBox[] = [];
  let cur: BoundsBox | null = null;
  let run = 0;
  const pad = (b: BoundsBox): BoundsBox => {
    const midLat = (b.north + b.south) / 2;
    const dLat = padKm / KM_PER_DEG_LAT;
    const dLng = padKm / (KM_PER_DEG_LAT * Math.max(0.2, Math.cos((midLat * Math.PI) / 180)));
    return { north: b.north + dLat, south: b.south - dLat, east: b.east + dLng, west: b.west - dLng };
  };
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (!cur) cur = { north: p.lat, south: p.lat, east: p.lng, west: p.lng };
    cur.north = Math.max(cur.north, p.lat);
    cur.south = Math.min(cur.south, p.lat);
    cur.east = Math.max(cur.east, p.lng);
    cur.west = Math.min(cur.west, p.lng);
    if (i > 0) {
      const q = points[i - 1];
      run += haversineDistanceKm(q.lat, q.lng, p.lat, p.lng);
    }
    if (run >= chunkKm && i < points.length - 1) {
      boxes.push(pad(cur));
      // Next box starts at this vertex so the chunks overlap, never gap.
      cur = { north: p.lat, south: p.lat, east: p.lng, west: p.lng };
      run = 0;
    }
  }
  if (cur) boxes.push(pad(cur));
  return boxes;
}

/** A box cut into four (retry for a box the endpoint refuses as too full). */
export function splitBox(b: BoundsBox): BoundsBox[] {
  const midLat = (b.north + b.south) / 2;
  const midLng = (b.east + b.west) / 2;
  return [
    { north: b.north, south: midLat, east: midLng, west: b.west },
    { north: b.north, south: midLat, east: b.east, west: midLng },
    { north: midLat, south: b.south, east: midLng, west: b.west },
    { north: midLat, south: b.south, east: b.east, west: midLng },
  ];
}

/**
 * Where a point sits against a line: how far off it, how far along, and
 * whether it's behind the start or past the end (projection clamps there).
 */
export function measureAgainstLine(p: LatLng, line: LatLng[]): { offKm: number; alongKm: number; beyondEnds: boolean } {
  if (line.length === 1) {
    return { offKm: haversineDistanceKm(p.lat, p.lng, line[0].lat, line[0].lng), alongKm: 0, beyondEnds: true };
  }
  let best = Infinity;
  let bestAlong = 0;
  let bestBeyond = false;
  let run = 0;
  const kmLng = KM_PER_DEG_LAT * Math.cos((p.lat * Math.PI) / 180);
  for (let i = 0; i < line.length - 1; i += 1) {
    const a = line[i];
    const b = line[i + 1];
    const segKm = haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
    const d = pointToSegmentKm(p, a, b);
    if (d < best) {
      const ax = a.lng * kmLng, ay = a.lat * KM_PER_DEG_LAT;
      const bx = b.lng * kmLng, by = b.lat * KM_PER_DEG_LAT;
      const px = p.lng * kmLng, py = p.lat * KM_PER_DEG_LAT;
      const dx = bx - ax, dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      const tRaw = lenSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lenSq;
      const t = Math.max(0, Math.min(1, tRaw));
      best = d;
      bestAlong = run + t * segKm;
      bestBeyond = (i === 0 && tRaw < 0) || (i === line.length - 2 && tRaw > 1);
    }
    run += segKm;
  }
  return { offKm: best, alongKm: bestAlong, beyondEnds: bestBeyond };
}

/**
 * Camps within `widthKm` of the line, in the order you reach them. Anything
 * behind the start or past the end only counts within `endSlackKm` of that
 * stop — the camps on the far side of town belong to the next leg.
 */
export function campsAlongLine(
  options: CampOption[],
  line: LatLng[],
  widthKm: number,
  endSlackKm = 2
): CampOnLine[] {
  const seen = new Set<number>();
  const out: CampOnLine[] = [];
  for (const c of options) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    const m = measureAgainstLine(c, line);
    if (m.offKm > widthKm) continue;
    if (m.beyondEnds && m.offKm > endSlackKm) continue;
    out.push({ ...c, offKm: m.offKm, alongKm: m.alongKm });
  }
  return out.sort((a, b) => a.alongKm - b.alongKm);
}

/** Total length of a line, km. */
export function lineKm(points: LatLng[]): number {
  let km = 0;
  for (let i = 1; i < points.length; i += 1) {
    km += haversineDistanceKm(points[i - 1].lat, points[i - 1].lng, points[i].lat, points[i].lng);
  }
  return km;
}
